import { type FC, useState } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';

import ActionModal, { type ActionModalProps } from './ActionModal';
import { type BurnNftStatus } from '../../hooks/useBurnNft';
import { EditionsCounter } from 'dibs-crypto-utils/exports/EditionsCounter';

import styles from './BurnNftModal.scss';

export const BURN_NFT = 'BURN_NFT';

export const burnModalCopy = defineMessages({
    initialTitle: { id: 'nft.create.burnToken.title', defaultMessage: 'Burn' },
    loadingTitle: { id: 'nft.create.burnToken.loadingTitle', defaultMessage: 'Burning' },
    successTitle: {
        id: 'nft.create.burnToken.successTitle',
        defaultMessage: 'Burned',
    },
    initial: {
        id: 'nft.create.burnToken.initial',
        defaultMessage:
            'Are you sure you want to proceed? This action is permanent and cannot be undone.',
    },
    initial2: {
        id: 'nft.create.burnToken.initial2',
        defaultMessage:
            'Burned NFTs are unrecoverable and will not be able to be transferred or sold. They will no longer appear on 1stDibs and other NFT platforms. Gas fees will apply.',
    },
    burnCta: { id: 'nft.create.burnToken.burnCta', defaultMessage: 'Burn' },
    loading: {
        id: 'nft.create.burnToken.body.loading',
        defaultMessage:
            "We are burning your {burnBalance, plural, one {NFT} other {NFTs}}. This could take a few minutes. You do not need to leave this page open for the process to complete successfully. Once the process is complete, we'll redirect you to your profile.",
    },
    loadingToast: {
        id: 'nft.create.burnToken.body.loadingToast',
        defaultMessage:
            "We are burning your {affectedEditions, plural, one {NFT} other {NFTs}}. This could take a few minutes. Once the process is complete, we'll redirect you to your profile.",
    },
    success: {
        id: 'nft.create.burnToken.body.success',
        defaultMessage:
            "Your {burnBalance, plural, one {NFT is} other {NFTs are}} unrecoverable on the Ethereum blockchain and can no longer be edited. Once you close this window, we'll redirect you to your profile.",
    },
    successToast: {
        id: 'nft.create.burnToken.body.successToast',
        defaultMessage:
            'Your {affectedEditions, plural, one {NFT is} other {NFTs are}} unrecoverable on the Ethereum blockchain and can no longer be edited.',
    },
    maxBalance: {
        id: 'nft.create.burnToken.body.maxBalance',
        defaultMessage:
            '<bold>You have {currentBalance, plural, one {# edition} other {# editions}} available to burn.</bold>',
    },
    maxBalanceCannotExceed: {
        id: 'nft.create.burnToken.body.maxBalance.many',
        defaultMessage: 'The quantity cannot exceed {currentBalance}.',
    },
    editionsCounterLabel: {
        id: 'nft.create.burnToken.body.editionsCounterLabel',
        defaultMessage: 'Quantity to Burn',
    },
});

export type BurnProps = {
    onClick: (editions: number) => void;
    onClose: ActionModalProps['onClose'];
    status: BurnNftStatus;
    nftItem: ActionModalProps['nftItem'];
    viewer: ActionModalProps['viewer'];
    currentBalance: number | null;
};

const BurnNftModal: FC<BurnProps> = ({ status, onClick, currentBalance, ...modalProps }) => {
    const { formatMessage } = useIntl();
    const [burnBalance, setBurnBalance] = useState<number | null>(1);
    const messageKey = status || 'initial';

    const validBurn =
        currentBalance === null ||
        (burnBalance && !isNaN(burnBalance) && burnBalance >= 1 && burnBalance <= currentBalance);

    return (
        <ActionModal
            {...modalProps}
            status={status}
            title={formatMessage(burnModalCopy[`${messageKey}Title`])}
            ctaOnClick={() => onClick(burnBalance || 1)}
            cta={!status ? formatMessage(burnModalCopy.burnCta) : undefined}
            ctaDisabled={!validBurn}
        >
            <p>
                {formatMessage(burnModalCopy[messageKey], { burnBalance })}
                {status === null && ' '}
                {status === null && formatMessage(burnModalCopy.initial2)}
            </p>
            {currentBalance && status === null && (
                <EditionsCounter
                    balance={burnBalance}
                    maxBalance={currentBalance}
                    setBalance={setBurnBalance}
                    label={formatMessage(burnModalCopy.editionsCounterLabel)}
                    maxBalanceMessage={
                        <>
                            {formatMessage(burnModalCopy.maxBalance, {
                                currentBalance,
                                bold: chunks => <span className={styles.boldText}>{chunks}</span>,
                            })}
                            {currentBalance > 1 && (
                                <>
                                    {' '}
                                    {formatMessage(burnModalCopy.maxBalanceCannotExceed, {
                                        currentBalance,
                                    })}
                                </>
                            )}
                        </>
                    }
                />
            )}
        </ActionModal>
    );
};

export default BurnNftModal;
